<template>
    <div class="settings-page">
        <!-- Header Bar -->
        <div class="settings-header">
            <div class="settings-header-left">
                <div class="settings-icon-wrap">
                    <i class="fas fa-cog"></i>
                </div>
                <h2 class="settings-title">Settings</h2>
                <span class="settings-count" v-if="settings.length">{{ settings.length }}</span>
            </div>
            <AllowPermission :permission="PERMISSION_CODES.CREATE_SETTINGS">
                <button class="settings-add-btn" @click="toggleForm()">
                    <i :class="showForm && !editMode ? 'fas fa-times' : 'fas fa-plus'"></i>
                    {{ showForm && !editMode ? 'Close' : 'Add Setting' }}
                </button>
            </AllowPermission>
        </div>

        <!-- Create / Edit Form -->
        <div class="settings-form-card" v-if="showForm">
            <Form
                :fields="createFields"
                title="Settings"
                submitUrl="/settings"
                updateRootUrl="/settings"
                @success="handleFormSuccess"
                @cancel="closeForm"
                :editMode="editMode"
                :editData="editData"
            />
        </div>

        <!-- Loading -->
        <div v-if="loading" class="settings-loading">
            <div class="settings-spinner"></div>
            <span>Loading settings...</span>
        </div>

        <!-- Empty -->
        <div v-else-if="!settings.length" class="settings-empty">
            <div class="settings-empty-icon"><i class="fas fa-cog"></i></div>
            <h3>No settings found</h3>
            <p>Add a setting to get started</p>
        </div>

        <!-- Cards Grid -->
        <div v-else class="settings-grid">
            <div
                class="setting-card"
                v-for="(item, idx) in settings"
                :key="item.id"
                :style="{ animationDelay: `${idx * 0.02}s` }"
            >
                <div class="setting-card-top">
                    <div class="setting-card-icon" :class="getSettingColor(idx)">
                        <i class="fas fa-sliders-h"></i>
                    </div>
                    <div class="setting-card-info">
                        <h4 class="setting-card-name">{{ item.name }}</h4>
                    </div>
                </div>
                <div class="setting-card-value">
                    <span class="setting-card-label">Value</span>
                    <span class="setting-card-val">{{ item.value }}</span>
                </div>
                <div class="setting-card-actions">
                    <AllowPermission :permission="PERMISSION_CODES.EDIT_SETTINGS">
                        <button class="setting-action-btn setting-btn-edit" @click="enableEditing(item)">
                            <i class="fas fa-pen"></i> Edit
                        </button>
                    </AllowPermission>
                    <AllowPermission :permission="PERMISSION_CODES.DELETE_SETTINGS">
                        <button class="setting-action-btn setting-btn-delete" @click="deleteSetting(item.id)">
                            <i class="fas fa-trash-alt"></i> Delete
                        </button>
                    </AllowPermission>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Form from "../../components/crud/form.vue"
import { createField, createColumn } from "../../utils/common"
import { http } from "@/utils/http"
import { PERMISSION_CODES } from "../../store/permission"
import AllowPermission from "../../components/layout/AllowPermission.vue"

export default {
    data() {
        return {
            PERMISSION_CODES,
            loading: true,
            showForm: false,
            editMode: false,
            editData: null,
            settings: [],
            createFields: [
                createField("Name*", "name", "text", true),
                // Hidden code field - auto-generated from name
                createField("Code", "code", "hidden", false),
                createField("Description*", "value", "text", true),
            ],
        }
    },
    components: { Form, AllowPermission },
    async mounted() {
        await this.fetchSettings()
        this.loading = false
    },
    methods: {
        getSettingColor(idx) {
            const colors = ["setting-clr-blue", "setting-clr-purple", "setting-clr-teal", "setting-clr-amber", "setting-clr-rose"]
            return colors[idx % colors.length]
        },

        toggleForm() {
            if (this.showForm && !this.editMode) {
                this.closeForm()
            } else {
                this.editMode = false
                this.editData = null
                this.showForm = true
            }
        },

        closeForm() {
            this.showForm = false
            this.editMode = false
            this.editData = null
        },

        handleFormSuccess() {
            this.closeForm()
            this.fetchSettings()
        },

        enableEditing(item) {
            this.editMode = true
            this.editData = { ...item }
            this.showForm = true
            window.scrollTo({ top: 0, behavior: "smooth" })
        },

        async fetchSettings() {
            try {
                const res = await http.get("/settings")
                this.settings = res.data.result || res.data || []
            } catch (err) {
                console.log("error in fetching settings", err)
            }
        },

        async deleteSetting(id) {
            if (!confirm("Are you sure you want to delete this setting?")) return
            try {
                await http.delete(`/settings/${id}`)
                this.$toast.success("Setting deleted successfully")
                this.settings = this.settings.filter((s) => s.id !== id)
            } catch (err) {
                console.log("error deleting setting", err)
                this.$toast.error("Error deleting setting")
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.settings-page {
    animation: settingsFadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
@keyframes settingsFadeIn {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
}

/* ========== HEADER ========== */
.settings-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    padding: 10px 16px;
    margin-bottom: 12px;
    box-shadow: $shadowSm;
    flex-wrap: wrap;
}
.settings-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
}
.settings-icon-wrap {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: linear-gradient(135deg, $primaryLight, darken($primaryLight, 4%));
    color: $primaryColor;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
}
.settings-title {
    font-size: 15px;
    font-weight: 700;
    color: $gray800;
    margin: 0;
}
.settings-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: $radiusFull;
    font-size: 11px;
    font-weight: 700;
    background: $primaryColor;
    color: white;
}
.settings-add-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border: none;
    border-radius: 6px;
    background: $primaryColor;
    color: white;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all $transitionFast;
    font-family: inherit;

    &:hover { background: $primaryDark; }
}

/* ========== FORM ========== */
.settings-form-card {
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 12px;
    box-shadow: $shadowSm;
}

/* ========== LOADING / EMPTY ========== */
.settings-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 64px 16px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    box-shadow: $shadowSm;

    span { font-size: 13px; color: $gray400; font-weight: 500; }
}
.settings-spinner {
    width: 24px;
    height: 24px;
    border: 2.5px solid $gray200;
    border-top-color: $primaryColor;
    border-radius: 50%;
    animation: settingsSpin 0.7s linear infinite;
}
@keyframes settingsSpin { to { transform: rotate(360deg); } }

.settings-empty {
    text-align: center;
    padding: 64px 16px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    box-shadow: $shadowSm;

    h3 { font-size: 14px; font-weight: 700; color: $gray600; margin: 0 0 4px; }
    p { font-size: 12px; color: $gray400; margin: 0; }
}
.settings-empty-icon {
    width: 48px;
    height: 48px;
    border-radius: $radiusFull;
    background: $gray50;
    border: 1px solid $gray100;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 12px;

    i { font-size: 20px; color: $gray300; }
}

/* ========== CARD GRID ========== */
.settings-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
}
.setting-card {
    background: white;
    border: 1px solid $gray100;
    border-radius: 10px;
    padding: 14px;
    box-shadow: $shadowSm;
    display: flex;
    flex-direction: column;
    gap: 10px;
    animation: settingSlideIn 0.25s cubic-bezier(0.4, 0, 0.2, 1) both;
    transition: all $transitionFast;

    &:hover {
        border-color: $gray200;
        box-shadow: $shadowMd;
        transform: translateY(-1px);
    }
}
@keyframes settingSlideIn {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
}
.setting-card-top {
    display: flex;
    align-items: flex-start;
    gap: 10px;
}
.setting-card-icon {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    flex-shrink: 0;
}
.setting-clr-blue { background: #eff6ff; color: #2563eb; }
.setting-clr-purple { background: #f5f3ff; color: #7c3aed; }
.setting-clr-teal { background: #f0fdfa; color: #0d9488; }
.setting-clr-amber { background: #fffbeb; color: #d97706; }
.setting-clr-rose { background: #fff1f2; color: #e11d48; }

.setting-card-info {
    flex: 1;
    min-width: 0;
}
.setting-card-name {
    font-size: 13.5px;
    font-weight: 700;
    color: $gray800;
    margin: 0;
    line-height: 1.3;
    word-break: break-word;
    padding-top: 6px;
}
.setting-card-value {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px 10px;
    background: $gray50;
    border-radius: 6px;
    border: 1px solid $gray100;
}
.setting-card-label {
    font-size: 10px;
    font-weight: 600;
    color: $gray400;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.setting-card-val {
    font-size: 13px;
    color: $gray700;
    font-weight: 500;
    line-height: 1.4;
    word-break: break-word;
}
.setting-card-actions {
    display: flex;
    gap: 6px;
    margin-top: auto;
    padding-top: 6px;
    border-top: 1px solid $gray100;
}
.setting-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border: 1px solid $gray200;
    border-radius: 5px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: all $transitionFast;
    font-family: inherit;
    background: white;

    i { font-size: 10px; }
}
.setting-btn-edit {
    color: $primaryColor;
    &:hover { background: $primaryLight; border-color: rgba(0, 94, 137, 0.15); }
}
.setting-btn-delete {
    color: $danger;
    &:hover { background: #fef2f2; border-color: rgba(220, 38, 38, 0.15); }
}

/* ========== RESPONSIVE ========== */
@media (max-width: 992px) {
    .settings-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 768px) {
    .settings-header { padding: 10px 12px; }
    .settings-grid { gap: 8px; }
    .setting-card { padding: 12px; }
}
@media (max-width: 480px) {
    .settings-grid { grid-template-columns: 1fr; }
    .setting-card { padding: 10px; }
    .setting-card-name { font-size: 13px; }
}
</style>
